import * as React from "react";
import { useEffect, useState } from "react";
import {
    Accordion,
    AccordionItem,
    AccordionItemButton,
    AccordionItemPanel,
} from "react-accessible-accordion";
import {
    BurdeSectionResponse,
    BurdeSingleSectionResponse,
    CalendarState,
    ImageDetails,
} from "burde";
import { DefaultCulture } from "ui/constants";
import { getCoverSectionById } from "ui/service";
import { Spinner } from "common/spinner/spinner";
import ColorPicker from "common/color-picker/color-picker";
import CoverGallery from "../cover-image-picker/cover-gallery";
import * as styles from "./cover-selector.css";
import { ApplicationState } from "store";
import {
    getInitialFrontCoverSectionId,
    getInitialBackCoverSectionId,
} from "burde-api-content/store";
import { connect } from "react-redux";

type Props = {
    burdeCoverSections: BurdeSectionResponse | null;
    calendar: CalendarState;
    backCover: boolean;
    initialFrontCoverSectionId: number | null;
    initialBackCoverSectionId: number | null;
    coverImage: ImageDetails | null;
    backCoverImage: ImageDetails | null;
};

type Slug = {
    id: number;
    data: BurdeSingleSectionResponse | null;
};

const CoverSelector: React.FC<Props> = ({
    burdeCoverSections,
    calendar,
    backCover,
    initialFrontCoverSectionId,
    initialBackCoverSectionId,
    coverImage,
    backCoverImage,
}) => {
    const [slugs, setSlugs] = useState<Array<Slug>>([]);
    const [defaultSlugId, setDefaultSlugId] = useState<number>();

    useEffect(() => {
        if (burdeCoverSections !== null) {
            let defaultSlugId = burdeCoverSections.default;

            if (!backCover && initialFrontCoverSectionId !== null) {
                defaultSlugId = initialFrontCoverSectionId;
            } else if (backCover && initialBackCoverSectionId !== null) {
                defaultSlugId = initialBackCoverSectionId;
            }

            setDefaultSlugId(defaultSlugId);

            // Don't load the default slug twice if for some reason it's already loaded
            const defaultSlug = slugs.find((x) => x.id === defaultSlugId);

            if (defaultSlug === undefined) {
                const availableSection = burdeCoverSections.availableSections.find(
                    (x) => x.id === defaultSlugId,
                );
                const culture = calendar.culture
                    ? calendar.culture
                    : DefaultCulture;
                if (availableSection) {
                    loadSlug(defaultSlugId, culture, calendar.calendarSize);
                }
            }
        }
    }, [burdeCoverSections]);

    const makeSureSlugIsLoaded = (openItems: number[]) => {
        for (let i = 0; i < openItems.length; i++) {
            const currentSlug = slugs.find((x) => x.id === openItems[i]);

            if (currentSlug === undefined && burdeCoverSections !== null) {
                const availableSection = burdeCoverSections.availableSections.find(
                    (x) => x.id === openItems[i],
                );
                const culture = calendar.culture
                    ? calendar.culture
                    : DefaultCulture;
                if (availableSection) {
                    loadSlug(
                        availableSection.id,
                        culture,
                        calendar.calendarSize,
                    );
                }
            }
        }
    };

    const loadSlug = (id: number, culture: string, calendarSize: string) => {
        getCoverSectionById(id, culture, calendarSize).then((response) => {
            const newSlugs = [
                ...slugs,
                {
                    id: id,
                    data: response,
                },
            ];
            setSlugs(newSlugs);
        });
    };

    const getColorsFromSlug = (slug: Slug | undefined) => {
        if (
            slug !== undefined &&
            slug.data !== null &&
            slug.data.solidColors !== null
        ) {
            return slug.data.solidColors;
        }

        return [];
    };

    const getImageResourcesFromSlug = (slug: Slug | undefined) => {
        if (
            slug !== undefined &&
            slug.data !== null &&
            slug.data.imageResources !== null
        ) {
            return slug.data.imageResources;
        }

        return [];
    };

    const getUseCustomColorPickerFromSlug = (slug: Slug | undefined) => {
        if (slug !== undefined && slug.data !== null && slug.data.colorPicker) {
            return true;
        }

        return false;
    };

    return (
        <div>
            {burdeCoverSections !== null && defaultSlugId !== undefined && (
                <Accordion
                    allowZeroExpanded={true}
                    allowMultipleExpanded={false}
                    className={styles.accordion}
                    preExpanded={[defaultSlugId]}
                    onChange={(openItems: number[]) =>
                        makeSureSlugIsLoaded(openItems)
                    }>
                    {burdeCoverSections.availableSections.map((x) => {
                        return (
                            <AccordionItem
                                key={x.id}
                                className={styles.accordionItem}
                                uuid={x.id}>
                                <AccordionItemButton
                                    className={styles.accordionButton}>
                                    {x.title}
                                </AccordionItemButton>
                                <AccordionItemPanel
                                    className={styles.accordionPanel}>
                                    {slugs.find((y) => y.id === x.id) !==
                                    undefined ? (
                                        <>
                                            <ColorPicker
                                                colors={getColorsFromSlug(
                                                    slugs.find(
                                                        (y) => y.id === x.id,
                                                    ),
                                                )}
                                                selectedColor={
                                                    backCover
                                                        ? calendar.backCoverColor
                                                        : calendar.coverColor
                                                }
                                                useCustomColorPicker={getUseCustomColorPickerFromSlug(
                                                    slugs.find(
                                                        (y) => y.id === x.id,
                                                    ),
                                                )}
                                                backCover={backCover}
                                            />
                                            <CoverGallery
                                                imageResources={getImageResourcesFromSlug(
                                                    slugs.find(
                                                        (y) => y.id === x.id,
                                                    ),
                                                )}
                                                isBackPage={backCover}
                                                coverImage={coverImage}
                                                backCoverImage={backCoverImage}
                                            />
                                        </>
                                    ) : (
                                        <Spinner></Spinner>
                                    )}
                                </AccordionItemPanel>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            )}
        </div>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    initialFrontCoverSectionId: getInitialFrontCoverSectionId(state),
    initialBackCoverSectionId: getInitialBackCoverSectionId(state),
});

const ConnectedCoverSelector = connect(mapStateToProps, {})(CoverSelector);

export { ConnectedCoverSelector };
